import { useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRole } from '@/contexts/RoleContext';

const driverNavItems = [
  { path: '/', icon: 'ri-home-4-line', activeIcon: 'ri-home-4-fill', label: 'Accueil' },
  { path: '/tracking', icon: 'ri-navigation-line', activeIcon: 'ri-navigation-fill', label: 'Suivi' },
  { path: '/reports', icon: 'ri-file-chart-2-line', activeIcon: 'ri-file-chart-2-fill', label: 'Rapports' },
  { path: '/profile', icon: 'ri-user-line', activeIcon: 'ri-user-fill', label: 'Profil' },
];

const fleetNavItems = [
  { path: '/', icon: 'ri-home-4-line', activeIcon: 'ri-home-4-fill', label: 'Accueil' },
  { path: '/tracking', icon: 'ri-navigation-line', activeIcon: 'ri-navigation-fill', label: 'Suivi' },
  { path: '/reports', icon: 'ri-file-chart-2-line', activeIcon: 'ri-file-chart-2-fill', label: 'Rapports' },
  { path: '/profile', icon: 'ri-user-line', activeIcon: 'ri-user-fill', label: 'Profil' },
  { path: '/fleet', icon: 'ri-car-line', activeIcon: 'ri-car-fill', label: 'Flotte' },
];

function RoleIndicator({ onLogout }: { onLogout: () => void }) {
  const { role, driverName, fleetUser } = useRole();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => {
      if (ref.current) {
        const h = ref.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--role-bar-height', `${h}px`);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [role]);

  const roleConfig = {
    driver: {
      label: driverName || 'Conducteur',
      color: 'bg-blue-500',
      icon: 'ri-steering-2-line',
    },
    fleet_manager: {
      label: fleetUser || 'Fleet Manager',
      color: 'bg-emerald-500',
      icon: 'ri-shield-keyhole-line',
    },
  };

  if (!role) return null;
  const config = roleConfig[role];

  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 6px)',
        paddingBottom: '6px',
        background: 'var(--role-bar-bg, rgba(255,255,255,0.95))',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <div className="flex items-center gap-2">
        <div className={`w-5 h-5 flex items-center justify-center ${config.color} rounded-md`}>
          <i className={`${config.icon} text-white text-xs`}></i>
        </div>
        <span className="text-xs font-semibold text-gray-700">{config.label}</span>
      </div>
      <button
        onClick={onLogout}
        className="flex items-center gap-1 text-[10px] text-gray-400 font-medium px-2 py-1 rounded-lg bg-gray-100 active:bg-gray-200"
      >
        <i className="ri-logout-box-line text-xs"></i>
        Changer de profil
      </button>
    </div>
  );
}

export default function BottomNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, logout } = useRole();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = role === 'fleet_manager' ? fleetNavItems : driverNavItems;
  const cols = navItems.length;
  const accentColor = role === 'fleet_manager' ? 'text-emerald-600' : 'text-blue-600';

  return (
    <>
      <RoleIndicator onLogout={handleLogout} />
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 z-50 shadow-[0_-1px_8px_rgba(0,0,0,0.06)]">
        <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {navItems.map((item, idx) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={`${item.path}-${idx}`}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-1 transition-colors ${isActive ? accentColor : 'text-gray-400 dark:text-gray-500'}`}
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${isActive ? item.activeIcon : item.icon} text-xl`}></i>
                </div>
                <span className={`text-[9px] font-semibold tracking-wide ${isActive ? accentColor : 'text-gray-400'}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
